import { useEffect, useState } from 'react';

import { getSnapshot, getValidToken, subscribe, ThumbnailToken } from './signed-thumbnail.store';

/**
 * Check 1 (proactive): ensures a fresh token exists BEFORE thumbnails render.
 * Returns null while the very first fetch is in flight — render a placeholder then.
 *
 * `enabled` gates the actual fetch: pass the user's authenticated state so the
 * token endpoint is only ever called once a session exists (fresh login, or an
 * already-authenticated session restored on page refresh).
 */
export function useThumbnailToken(enabled: boolean): ThumbnailToken | null {
  // React 17 has no useSyncExternalStore, so subscribe manually.
  const [token, setToken] = useState<ThumbnailToken | null>(getSnapshot);

  useEffect(() => {
    const unsubscribe = subscribe(() => setToken(getSnapshot()));
    // The store may have emitted between the initial render and this subscription.
    setToken(getSnapshot());
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Kicks off a (deduped) refresh when missing or stale; store emit re-renders us.
    getValidToken().catch(() => {
      /* surfaced via image placeholders; next mount retries */
    });
  }, [enabled, token]);

  return enabled ? token : null;
}
